import React from 'react';
import { createSelector } from '@reduxjs/toolkit';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { generatePath, useHistory } from 'react-router-dom';

import type { IResourceDefaultFolder, IResourceFolder, IResourceFolderType } from '@/api/types';
import type { IFolderType, ISimpleResourceType } from '@/components/common';
import type { ISelectedFolder } from '@/store/types';

import {
  addItemTranslationMap,
  classList,
  compareSelectedFolder,
  isChildrenSelected,
  isSimpleResourceFolder,
} from '@/helpers';
import { useAppSelector, useFolderModal, useResourceModal, useResourceNavigation } from '@/hooks';
import { DropdownMenu } from '@/components/common/DropdownMenu';
import { selectFoldersList } from '@/store/selectors/resourcesSelectors';
import { Z_INDEX_MODAL } from '@/config/variables';
import { selectUserType } from '@/store/selectors/authSelectors';
import { UserType } from '@/store/enums';
import { Home } from '@/assets/icons';
import { TreeFolderAccordion } from '.';
import { FolderModalType, ResourceModalType } from '@/hooks/enums';
import routes from '@/config/routes';

interface IProps {
  className?: string;
  data: IResourceDefaultFolder | IResourceFolder | IResourceFolderType;
  resourceDetails: {
    clientId: number;
    folderType: IFolderType;
    parentFolderId: number | null;
  };
  wrapWithParent?: boolean;
  eventKey?: number;
}

const folderItemSelector = createSelector(selectFoldersList, selectUserType, (foldersList, userType) => ({
  foldersList,
  userType,
}));

const FolderItem = ({
  data,
  resourceDetails,
  className,
  wrapWithParent = true,
  eventKey,
}: IProps): React.ReactElement => {
  const { t } = useTranslation();
  const { selectedFolder } = useResourceNavigation();
  const { foldersList, userType } = useAppSelector(folderItemSelector);
  const { handleOpenModal: handleOpenResourceModal } = useResourceModal();
  const { handleOpenModal: handleOpenFolderModal } = useFolderModal();
  const history = useHistory();

  const folderSignature: ISelectedFolder = {
    clientId: resourceDetails.clientId,
    folderId: data.folderId,
    folderType: resourceDetails.folderType,
  };
  const isSelected = !!selectedFolder && compareSelectedFolder(selectedFolder, folderSignature);

  const handleOnSelect = () => {
    if (!isSelected) {
      if (resourceDetails.folderType === 'home') {
        history.push(routes.sp.scheduling.resources.index);
        return;
      }

      history.push(
        generatePath(routes.sp.scheduling.resources.folder, {
          client: resourceDetails.clientId,
          resourceType: resourceDetails.folderType,
          folderId: data.folderId,
        }),
      );
    }
  };

  const nestedFolders = ((isSimpleResourceFolder(data) && data.folders) || []).map((folder, id) => {
    return (
      <FolderItem
        key={`${id}_${folder.folderId}`}
        data={folder}
        resourceDetails={{ ...resourceDetails, parentFolderId: data.folderId }}
        wrapWithParent={Boolean(folder.folders?.length)}
        eventKey={id}
      />
    );
  });

  return (
    <TreeFolderAccordion
      title={data.name}
      onSelectCallback={handleOnSelect}
      className={classList(className, isSelected && 'accordion-title-selected')}
      wrapWithParent={wrapWithParent}
      eventKey={eventKey}
      isChildrenSelected={
        selectedFolder ? isChildrenSelected(folderSignature, selectedFolder, foldersList || []) : false
      }
      icon={resourceDetails.folderType === 'home' ? Home : ''}
      headerSlot={
        resourceDetails.folderType !== 'home' &&
        userType === UserType.client && (
          <>
            <DropdownMenuStyled
              options={[
                {
                  label: t`resources.content.addFolder`,
                  callback: e => {
                    e.stopPropagation();
                    handleOpenFolderModal({
                      modalType: FolderModalType.new,
                      data: {
                        folderType: folderSignature.folderType as ISimpleResourceType,
                        parentFolderId: folderSignature.folderId,
                        clientId: folderSignature.clientId,
                      },
                    });
                  },
                },
                {
                  label: t(addItemTranslationMap[resourceDetails.folderType]),
                  callback: e => {
                    e.stopPropagation();
                    handleOpenResourceModal({
                      modalType: ResourceModalType.new,
                      data: {
                        resourceType: resourceDetails.folderType as ISimpleResourceType,
                        clientId: folderSignature.clientId,
                        parentFolderId: folderSignature.folderId,
                        existingResource: null,
                      },
                    });
                  },
                },
                {
                  label: t`common.btnEdit`,
                  callback: e => {
                    e.stopPropagation();
                    handleOpenFolderModal({
                      modalType: FolderModalType.edit,
                      data: {
                        folderType: folderSignature.folderType as ISimpleResourceType,
                        clientId: folderSignature.clientId,
                        folderId: folderSignature.folderId,
                        name: data.name,
                        agentVisible: (data as IResourceFolder).agentVisible,
                        parentFolderId: resourceDetails.parentFolderId,
                      },
                    });
                  },
                },
                {
                  label: t`common.btnDelete`,
                  callback: e => {
                    e.stopPropagation();
                    handleOpenFolderModal({
                      modalType: FolderModalType.delete,
                      data: {
                        folderType: folderSignature.folderType as ISimpleResourceType,
                        clientId: folderSignature.clientId,
                        folderId: folderSignature.folderId,
                        name: data.name,
                        parentFolderId: resourceDetails.parentFolderId,
                      },
                    });
                  },
                },
              ]}
            />
          </>
        )
      }
    >
      {nestedFolders}
    </TreeFolderAccordion>
  );
};

export { FolderItem };

const DropdownMenuStyled = styled(DropdownMenu)`
  z-index: ${Z_INDEX_MODAL};
  width: 32px;
`;
